import {useRoute} from '@react-navigation/native';
import React from 'react';
import {useWindowDimensions, View} from 'react-native';
import {useQuery} from 'react-query';
import {AuctionParams} from '../../types/nav';
import {useFolder} from '../../hooks/useFolder';
import {Auction, FolderType, UserInfo} from '../../types';
import {getById, getFolderContent, getUserData} from '../../api';
import {useRouteContact} from '../../hooks/useRouteContact';
import ChatView from '../contacts/chatview';
import {
  ActivityIndicator,
  AnimatedFAB,
  Button,
  Chip,
  List,
} from 'react-native-paper';
import {useAuctionActions} from '../../hooks/useAuctionActions';
import {useRouteAuction} from '../../hooks/useRouteAuction';
import {useAppSelector} from '../../store/hooks';

export function AuctionPage() {
  const route = useRoute();
  const params = route.params as AuctionParams;
  const {auctionId} = params;
  const userQuery = useQuery('UserData', () => getUserData());
  const auctionQuery = useQuery(
    'auction_' + auctionId,
    async () => await getById(auctionId, auctionId),
  );
  const auction = auctionQuery.data as Auction;
  const isLoading = !userQuery.isFetched || !auctionQuery.isFetched;
  const user = userQuery?.data as UserInfo;
  const isOwner = !isLoading && user?.id === auction?.userId;
  const isWinner = !isLoading && user?.id === auction?.winnerId;
  const sellerReleasedDownload =
    !isLoading && auction?.encryptedKey !== undefined;
  const winnerPaid =
    !isLoading && auction?.winnerPaymentTransactionId !== undefined;
  const isClosed = auction?.isClosed;
  const isDownloading = useAppSelector(
    state => state.auction.downloadProgress,
  );
  let downloadText = isDownloading
    ? `Downloading ${parseInt((isDownloading * 100).toString(), 10)}%`
    : 'Download';
  const {
    createBid,
    closeAuction,
    currentBid,
    payForAuction,
    downloadAuctionMedia,
    releaseDownload,
    auctionTransaction,
  } = useAuctionActions(auctionId, auction, user);
  const confirmationText = !auctionTransaction
    ? 'Waiting For Confirmation'
    : (auctionTransaction.receipt?.status || 0) + ' Confirmation';
  useRouteAuction(auction, isLoading);
  const {height} = useWindowDimensions();
  return (
    <View style={{width: '100%', height: '100%'}}>
      <View style={{width: '100%', height: '40%'}}>
        {isLoading ? (
          <ActivityIndicator />
        ) : (
          <List.Section>
            <List.Item
              title={'Auction Name'}
              description={auction?.title}
            />
            <List.Item
              title={'Author Name'}
              description={auction?.authorName}
            />
            <List.Item
              title={'Auction Current Bid'}
              description={currentBid || auction?.minPrice}
            />
            <List.Item
              title={'Auction Minimum Step'}
              description={auction?.minStep}
            />
            <List.Item
              title={'Auction Minimum Price'}
              description={auction?.minPrice}
            />
          </List.Section>
        )}
        {!isOwner && !isClosed && (
          <Button
            icon={'plus'}
            onPress={createBid}
            mode={'outlined'}
            style={{
              bottom: 16,
              right: 16,
              position: 'absolute',
            }}>
            Make A Bid
          </Button>
        )}
        {isOwner && !isClosed && (
          <Button
            icon={'clipboard-check'}
            onPress={closeAuction}
            mode={'outlined'}
            style={{
              top: 16,
              right: 16,
              position: 'absolute',
            }}>
            Close Auction
          </Button>
        )}
        {isWinner && !winnerPaid && (
          <Button
            icon={'currency-usd'}
            onPress={payForAuction}
            mode={'outlined'}
            style={{
              bottom: 16,
              right: 16,
              position: 'absolute',
            }}>
            Pay
          </Button>
        )}
        {isWinner && sellerReleasedDownload && (
          <Button
            icon={'file'}
            onPress={downloadAuctionMedia}
            mode={'outlined'}
            style={{
              bottom: 16,
              right: 16,
              position: 'absolute',
            }}>
            {downloadText}
          </Button>
        )}

        {isOwner && winnerPaid && (
          <Button
            icon={'file'}
            onPress={releaseDownload}
            mode={'outlined'}
            disabled={!auctionTransaction || sellerReleasedDownload}
            style={{
              bottom: 16,
              right: 16,
              position: 'absolute',
            }}>
            Release File
          </Button>
        )}
        {isClosed && (
          <Chip
            style={{
              top: 16,
              right: 16,
              position: 'absolute',
            }}
            icon={'clipboard-check'}>
            {' '}
            Auction Closed{' '}
          </Chip>
        )}
        {isClosed && winnerPaid && (
          <Chip
            style={{
              top: 50,
              right: 16,
              position: 'absolute',
            }}
            icon={'timer-cog-outline'}>
            {confirmationText}
          </Chip>
        )}
      </View>
      <View style={{width: '100%', height: '60%'}}>
        <ChatView
          params={{user, contact: auction}}
          height={(height / 100) * 60}
          chatId={auctionId}
        />
      </View>
    </View>
  );
}
